import React, { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { Info } from '@mui/icons-material';
import { CircularProgress, TextField } from '@mui/material';

import * as S from './styles';
import Button from 'components/Button';
import Text from 'components/Text';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';
import { useToast } from 'hooks/useToast';
import GlobalModal from 'modals/GlobalModal/GlobalModal';
import {
  formatWhatsAppNumber,
  sendWhatsAppRecoveryRequest,
  validateWhatsAppNumber,
} from 'services/n8nWebhookService';
import { useIsMobile } from 'utils/mediaQueries';

// Country configuration
interface Country {
  code: string;
  dialCode: string;
  flag: string;
  name: string;
}

const COUNTRIES: Country[] = [
  { code: 'BR', dialCode: '+55', flag: '🇧🇷', name: 'Brasil' },
];

interface WhatsAppRecoveryModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (partialEmail: string) => void;
}

const whatsappSchema = yup.object().shape({
  whatsapp: yup
    .string()
    .required('Número de WhatsApp é obrigatório')
    .test('is-valid-whatsapp', 'Número de WhatsApp inválido', value => {
      if (!value) return false;
      const formatted = formatWhatsAppNumber(value);
      return validateWhatsAppNumber(formatted);
    }),
});

const WhatsAppRecoveryModal: React.FC<WhatsAppRecoveryModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const theme = useThemeContext();
  const { toastError, toastSuccess } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country>(COUNTRIES[0]);
  const isMobile = useIsMobile();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(whatsappSchema),
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: { whatsapp: string }) => {
    setLoading(true);
    try {
      // Add country code if not present and format WhatsApp number
      let numberWithCode = data.whatsapp;
      if (!numberWithCode.startsWith('+')) {
        numberWithCode = `${selectedCountry.dialCode}${numberWithCode}`;
      }
      const formattedNumber = formatWhatsAppNumber(numberWithCode);

      console.log('[WhatsAppRecovery] Sending recovery request for:', formattedNumber);

      // Send request to n8n webhook
      const response = await sendWhatsAppRecoveryRequest(formattedNumber);

      if (response.success) {
        toastSuccess(
          'Código de confirmação enviado! Verifique seu WhatsApp e e-mail.'
        );

        // Close this modal and proceed to code validation
        handleClose();

        // Pass the WhatsApp number to parent so it can show code validation modal
        onSuccess(formattedNumber);
      } else {
        toastError(
          response.message ||
            'Não foi possível recuperar seu e-mail. Verifique o número do WhatsApp.'
        );
      }
    } catch (error: any) {
      console.error('[WhatsAppRecovery] Error:', error);
      toastError(
        error.message || 'Erro ao solicitar recuperação. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <GlobalModal
      title="RECUPERAÇÃO DE E-MAIL"
      open={open}
      onClose={handleClose}
      width={isMobile ? '90%' : '500px'}
    >
      <S.Content>
        <S.InfoText>
          <Text size="medium" color="textPrimary">
            Esqueceu seu e-mail de cadastro?
          </Text>
          <Text size="small" color="textSecondary">
            Informe o número de WhatsApp que você usou durante o cadastro na plataforma.
            Enviaremos um código de confirmação e ajudaremos você a recuperar seu e-mail.
          </Text>
        </S.InfoText>

        <S.FormContainer onSubmit={handleSubmit(onSubmit)}>
          <S.PhoneInputContainer>
            <S.CountrySelector type="button" disabled={loading} onClick={(e) => e.preventDefault()}>
              <S.CountryFlag>{selectedCountry.flag}</S.CountryFlag>
              <S.CountryCode>{selectedCountry.dialCode}</S.CountryCode>
            </S.CountrySelector>

            <Controller
              name="whatsapp"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Número de WhatsApp"
                  placeholder="11 99999-9999"
                  fullWidth
                  error={!!errors.whatsapp}
                  helperText={errors.whatsapp?.message}
                  size={isMobile ? 'small' : 'medium'}
                  disabled={loading}
                  inputProps={{
                    inputMode: 'tel',
                  }}
                />
              )}
            />
          </S.PhoneInputContainer>

          <S.HelpText>
            <Info fontSize="small" style={{ color: theme.info || '#2196f3' }} />
            <Text size="small" color="textSecondary">
              O código do país {selectedCountry.flag} {selectedCountry.dialCode} já está selecionado.
              Digite apenas o DDD e número. Exemplo: 11 99999-9999
            </Text>
          </S.HelpText>

          <S.ButtonContainer>
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={handleClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: theme.primary }} />
              ) : (
                'Enviar Código'
              )}
            </Button>
          </S.ButtonContainer>
        </S.FormContainer>

        <div style={{ textAlign: 'center' }}>
          <Text size="small" color="textSecondary">
            Não tem mais acesso ao WhatsApp? Clique no ícone do WhatsApp no canto inferior direito da página para entrar em contato com o suporte.
          </Text>
        </div>
      </S.Content>
    </GlobalModal>
  );
};

export default WhatsAppRecoveryModal;
